// Maps admin-editable content keys to their built-in default data, so the
// admin editor can display current content and reset to it.
import { CASE_SCENARIOS } from "@/data/cases";
import { CLINPATH_CASES } from "@/data/clinPath";
import { DENTAL_CASES } from "@/data/dentalCases";
import { NUTRITION_CASES } from "@/data/nutritionCases";
import { TRIAGE_SCENARIOS } from "@/data/triage";
import { URINALYSIS_CASES } from "@/data/urinalysis";
import { ADMISSION_CASES } from "@/data/admission";
import { WARD_ROUNDS_CASES } from "@/data/wardRounds";
import { BLOOD_CASES } from "@/data/bloodReport";
import { SIM_CONFIGS } from "@/data/simConfigs";
import { FURTHER_READING } from "@/data/furtherReading";

// Simulator case libraries the admin can edit (kind = "cases").
export const CASE_REGISTRY = [
  { key: "triage", label: "Ring-In Triage Console", section: "Triage & Admission", defaults: TRIAGE_SCENARIOS },
  { key: "admission", label: "Admissions Desk", section: "Triage & Admission", defaults: ADMISSION_CASES },
  { key: "emergency", label: "Emergency First Aid & CPR", section: "Triage & Admission", defaults: SIM_CONFIGS.emergency.cases },
  { key: "anaesthesia", label: "Anaesthetic Case Library", section: "Surgery & Anaesthesia", defaults: CASE_SCENARIOS },
  { key: "anaesthesia2", label: "Anaesthetic Case Library (V2)", section: "Surgery & Anaesthesia", defaults: CASE_SCENARIOS },
  { key: "asepsis", label: "Surgical Asepsis & Instrument Prep", section: "Surgery & Anaesthesia", defaults: SIM_CONFIGS.asepsis.cases },
  { key: "radiography", label: "Radiography Positioning & Safety", section: "Surgery & Anaesthesia", defaults: SIM_CONFIGS.radiography.cases },
  { key: "anaescrisis", label: "Anaesthetic Complications & Crisis Response", section: "Surgery & Anaesthesia", defaults: SIM_CONFIGS.anaescrisis.cases },
  { key: "clinpath", label: "Clinical Pathology Lab", section: "Clinical Pathology", defaults: CLINPATH_CASES },
  { key: "urinalysis", label: "Urinalysis Bench", section: "Clinical Pathology", defaults: URINALYSIS_CASES },
  { key: "bloodwork", label: "Bloodwork Interpretation", section: "Clinical Pathology", defaults: BLOOD_CASES },
  { key: "wardrounds", label: "Ward Rounds & Medications", section: "Inpatient Care", defaults: WARD_ROUNDS_CASES },
  { key: "dispensing", label: "Dispensing & Medication Supply", section: "Inpatient Care", defaults: SIM_CONFIGS.dispensing.cases },
  { key: "infection", label: "Infection Control & Biosecurity", section: "Inpatient Care", defaults: SIM_CONFIGS.infection.cases },
  { key: "dental", label: "Dental Charting Trainer", section: "Surgery & Anaesthesia", defaults: DENTAL_CASES },
  { key: "nutrition", label: "Nutrition Calculator", section: "Nutrition & Wellbeing", defaults: NUTRITION_CASES },
  { key: "conflict", label: "Conflict Resolution", section: "Communication & Professional Practice", defaults: SIM_CONFIGS.conflict.cases },
  { key: "grief", label: "Grief & Euthanasia Support", section: "Communication & Professional Practice", defaults: SIM_CONFIGS.grief.cases },
  { key: "reception", label: "Reception & Client Service", section: "Communication & Professional Practice", defaults: SIM_CONFIGS.reception.cases },
];

export const CASE_BY_KEY = Object.fromEntries(CASE_REGISTRY.map((c) => [c.key, c]));

// Friendly labels for further-reading topics (kind = "reading").
const READING_LABELS = {
  anaesthesia: "Anaesthesia",
  fluids: "Fluid Therapy",
  ventilation: "IPPV / Ventilation",
  dosing: "Drug Dosing",
  admission: "Patient Admission",
  clinpath: "Clinical Pathology",
  urinalysis: "Urinalysis",
  wardrounds: "Ward Rounds & Medications",
  triage: "Phone Triage",
  dental: "Dental / COHAT",
  nutrition: "Nutrition",
  conflict: "Conflict Resolution",
  grief: "Grief & Euthanasia",
  reception: "Reception & Client Service",
  dispensing: "Dispensing & Supply",
  infection: "Infection Control",
  radiography: "Radiography",
  asepsis: "Surgical Asepsis",
  emergency: "Emergency & CPR",
};

export const READING_REGISTRY = Object.keys(FURTHER_READING).map((topic) => ({
  key: topic,
  label: READING_LABELS[topic] || topic,
  defaults: FURTHER_READING[topic],
}));

export const READING_BY_KEY = Object.fromEntries(READING_REGISTRY.map((r) => [r.key, r]));

export const deepClone = (v) => JSON.parse(JSON.stringify(v));
